/**
 * localStorage persistence for the pre-auth `NewsletterDraft`.
 *
 * The in-memory draft is the source of truth during a session; this module is
 * the durable mirror, written (debounced) on every change so a refresh, an
 * accidental close, or the signup round-trip never loses input. On mount the
 * wizard reads the current draft back and resumes it (see `furthestStep`).
 *
 * All access is defensive: a private/full/blocked localStorage must degrade to
 * "no persistence", never throw into the render tree.
 */
import { DRAFT_VERSION, type NewsletterDraft } from './draft';

export const CURRENT_DRAFT_KEY = 'nuff:currentDraftId';
export const DRAFT_KEY_PREFIX = 'nuff:draft:';

export function draftKey(draftId: string): string {
  return `${DRAFT_KEY_PREFIX}${draftId}`;
}

function storage(): Storage | null {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

/** Persist the draft and mark it current. Stamps `updatedAt` at write time. */
export function saveDraft(
  draft: NewsletterDraft,
  now: string = new Date().toISOString(),
): NewsletterDraft {
  const stamped: NewsletterDraft = { ...draft, updatedAt: now };
  const store = storage();
  if (!store) {
    return stamped;
  }
  try {
    store.setItem(draftKey(stamped.draftId), JSON.stringify(stamped));
    store.setItem(CURRENT_DRAFT_KEY, stamped.draftId);
  } catch {
    // Quota or blocked storage: keep going without persistence.
  }
  return stamped;
}

/** Minimal shape + version guard so a stale or corrupt entry is discarded. */
function isValidDraft(value: unknown): value is NewsletterDraft {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const draft = value as Partial<NewsletterDraft>;
  return (
    draft.version === DRAFT_VERSION &&
    typeof draft.draftId === 'string' &&
    typeof draft.purpose === 'object' &&
    Array.isArray(draft.focus) &&
    typeof draft.voice === 'object'
  );
}

/** Read the current draft back, or null if none / stale / unreadable. */
export function loadCurrentDraft(): NewsletterDraft | null {
  const store = storage();
  if (!store) {
    return null;
  }
  try {
    const id = store.getItem(CURRENT_DRAFT_KEY);
    if (!id) {
      return null;
    }
    const raw = store.getItem(draftKey(id));
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    return isValidDraft(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Remove a draft and clear the current pointer if it referenced it. */
export function clearDraft(draftId: string): void {
  const store = storage();
  if (!store) {
    return;
  }
  try {
    store.removeItem(draftKey(draftId));
    if (store.getItem(CURRENT_DRAFT_KEY) === draftId) {
      store.removeItem(CURRENT_DRAFT_KEY);
    }
  } catch {
    // Ignore: nothing we can do if storage is unavailable.
  }
}
